import React, { useState } from "react";
import {
  Accessibility, Minus, Plus, Contrast, BookOpen, X, RotateCcw,
  Type, MousePointer2, PauseCircle, Underline, ALargeSmall, Wind,
} from "lucide-react";
import { useAccessibility } from "../lib/accessibility";

function ToggleRow({ label, Icon, on, onClick, testid, hint }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      aria-pressed={on}
      className={`group w-full flex items-center justify-between gap-3 p-3 rounded-xl border transition-colors text-left ${
        on
          ? "bg-[color:var(--alpha-purple-50)] border-[color:var(--alpha-purple-300)] text-[color:var(--alpha-purple-800)]"
          : "bg-white/80 border-[color:var(--alpha-line)] hover:bg-[color:var(--alpha-bg-soft)]"
      }`}
    >
      <span className="flex items-center gap-2.5 text-sm font-medium leading-tight">
        <span className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${
          on ? "bg-[color:var(--alpha-purple-600)] text-white" : "bg-[color:var(--alpha-bg-soft)] text-[color:var(--alpha-purple-700)]"
        }`}>
          <Icon className="w-3.5 h-3.5" />
        </span>
        <span className="flex flex-col">
          <span>{label}</span>
          {hint && <span className="text-[11px] font-normal text-[color:var(--alpha-mute)] mt-0.5">{hint}</span>}
        </span>
      </span>
      <span className={`text-[10px] tracking-[0.18em] font-bold uppercase shrink-0 ${
        on ? "text-[color:var(--alpha-purple-700)]" : "text-[color:var(--alpha-mute)]"
      }`}>
        {on ? "On" : "Off"}
      </span>
    </button>
  );
}

export default function AccessibilityToolbar() {
  const [open, setOpen] = useState(false);
  const a = useAccessibility();

  return (
    <>
      {/* Floating button — bottom-right (chatbot is bottom-left) */}
      <button
        aria-label={open ? "Close accessibility tools" : "Open accessibility tools"}
        aria-expanded={open}
        aria-controls="alpha-a11y-panel"
        data-testid="a11y-toggle"
        onClick={() => setOpen((v) => !v)}
        className="fixed right-4 bottom-6 md:right-8 md:bottom-8 z-50 w-14 h-14 rounded-full alpha-gradient text-white shadow-2xl hover:scale-105 active:scale-95 transition-transform flex items-center justify-center focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[color:var(--alpha-purple-300)]"
      >
        {open ? <X className="w-6 h-6" /> : <Accessibility className="w-6 h-6" />}
        <span className="sr-only">Accessibility</span>
      </button>

      {open && (
        <div
          id="alpha-a11y-panel"
          role="dialog"
          aria-label="Accessibility tools"
          aria-modal="false"
          data-testid="a11y-panel"
          className="fixed right-4 left-4 sm:left-auto bottom-24 md:right-8 md:bottom-28 z-50 sm:w-[340px] alpha-glass rounded-2xl p-4 md:p-5 shadow-2xl border border-[color:var(--alpha-line-strong)] max-h-[calc(100vh-7rem)] overflow-y-auto"
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-3 pb-3 border-b border-[color:var(--alpha-line)]">
            <div>
              <div className="font-display font-semibold text-[color:var(--alpha-ink)] leading-tight text-[15px]">Accessibility</div>
              <div className="text-[11px] tracking-[0.18em] uppercase text-[color:var(--alpha-mute)] mt-0.5 font-semibold">
                Saved on this device
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              aria-label="Close accessibility panel"
              className="w-8 h-8 rounded-lg hover:bg-[color:var(--alpha-bg-soft)] flex items-center justify-center"
              data-testid="a11y-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Font size */}
          <div
            className="flex items-center justify-between gap-2 p-3 rounded-xl bg-white/80 border border-[color:var(--alpha-line)] mb-2"
            data-testid="a11y-fontsize-row"
          >
            <span className="text-sm font-medium flex items-center gap-2.5">
              <span className="w-7 h-7 rounded-lg bg-[color:var(--alpha-bg-soft)] text-[color:var(--alpha-purple-700)] flex items-center justify-center">
                <BookOpen className="w-3.5 h-3.5" />
              </span>
              Text size
            </span>
            <div className="flex items-center gap-1.5">
              <button
                onClick={a.dropFont}
                className="w-8 h-8 rounded-full border border-[color:var(--alpha-line)] flex items-center justify-center hover:bg-[color:var(--alpha-bg-soft)]"
                aria-label="Decrease text size"
                data-testid="a11y-font-dec"
                disabled={a.fontSizeIdx === 0}
              >
                <Minus className="w-3.5 h-3.5" />
              </button>
              <span
                className="text-[12px] font-bold w-8 text-center text-[color:var(--alpha-purple-700)]"
                data-testid="a11y-font-label"
              >
                {a.fontLabel}
              </span>
              <button
                onClick={a.bumpFont}
                className="w-8 h-8 rounded-full border border-[color:var(--alpha-line)] flex items-center justify-center hover:bg-[color:var(--alpha-bg-soft)]"
                aria-label="Increase text size"
                data-testid="a11y-font-inc"
                disabled={a.fontSizeIdx === 4}
              >
                <Plus className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Toggles */}
          <div className="space-y-2">
            <ToggleRow
              label="High contrast"
              hint="Stronger ink + bolder lines"
              Icon={Contrast}
              on={a.contrast}
              onClick={() => a.setContrast(!a.contrast)}
              testid="a11y-contrast"
            />
            <ToggleRow
              label="Reduced motion"
              hint="Calmer transitions"
              Icon={Wind}
              on={a.reducedMotion}
              onClick={() => a.setReducedMotion(!a.reducedMotion)}
              testid="a11y-reduced-motion"
            />
            <ToggleRow
              label="Pause animations"
              hint="Stop background motion"
              Icon={PauseCircle}
              on={a.pausedAnims}
              onClick={() => a.setPausedAnims(!a.pausedAnims)}
              testid="a11y-pause-anims"
            />
            <ToggleRow
              label="Dyslexia-friendly font"
              hint="Switch to OpenDyslexic"
              Icon={Type}
              on={a.dyslexia}
              onClick={() => a.setDyslexia(!a.dyslexia)}
              testid="a11y-dyslexia"
            />
            <ToggleRow
              label="Underline links"
              hint="Always visible"
              Icon={Underline}
              on={a.underlineLinks}
              onClick={() => a.setUnderlineLinks(!a.underlineLinks)}
              testid="a11y-underline-links"
            />
            <ToggleRow
              label="Increase text spacing"
              hint="More room between letters"
              Icon={ALargeSmall}
              on={a.textSpacing}
              onClick={() => a.setTextSpacing(!a.textSpacing)}
              testid="a11y-text-spacing"
            />
            <ToggleRow
              label="Larger cursor"
              hint="Easier to follow"
              Icon={MousePointer2}
              on={a.largeCursor}
              onClick={() => a.setLargeCursor(!a.largeCursor)}
              testid="a11y-large-cursor"
            />
            <ToggleRow
              label="Simple English mode"
              hint="Plain wording on supported pages"
              Icon={BookOpen}
              on={a.simple}
              onClick={() => a.setSimple(!a.simple)}
              testid="a11y-simple"
            />
          </div>

          {/* Footer actions */}
          <div className="mt-4 pt-3 border-t border-[color:var(--alpha-line)] flex items-center justify-between gap-2">
            <a
              href="/easy-read"
              className="text-[12px] font-semibold text-[color:var(--alpha-purple-700)] underline underline-offset-2 hover:text-[color:var(--alpha-purple-800)]"
              data-testid="a11y-easyread-link"
            >
              View Easy Read
            </a>
            <button
              onClick={a.reset}
              data-testid="a11y-reset"
              className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-[color:var(--alpha-mute)] hover:text-[color:var(--alpha-ink)]"
              aria-label="Reset accessibility preferences"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Reset
            </button>
          </div>

          <p className="text-[10.5px] text-[color:var(--alpha-mute)] mt-3 leading-relaxed">
            Preferences are stored on this device. We respect your system's reduced-motion setting too.
          </p>
        </div>
      )}
    </>
  );
}
